import "../styles/dashboard.css";
import { useDashboardApi } from "../api/dashboard";
import { formatDate } from "../util/date";
import { toast } from "react-toastify";
import DeliveryTracker from "../components/DeliveryTracker";
import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";
/* =========================
   COMPONENT
========================= */

export default function MaintenanceOverviewDashboard() {
  const {
    getMaintenanceOverview,
    sendMaintenanceReminder,
    getMaintenanceConfig,
    updateMaintenanceConfig,
    resolveMaintenanceNotification
  } = useDashboardApi();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔧 Maintenance Config
const [firstServiceAlert, setFirstServiceAlert] = useState(0);
const [serviceInterval, setServiceInterval] = useState(0);
const [warningWindow, setWarningWindow] = useState(0); // ✅ NEW

  // 🔍 Search
  const [search, setSearch] = useState("");

  // 📄 Pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  // ℹ️ Owner info dialog
  const [ownerDetails, setOwnerDetails] = useState(null);
  

  /* =========================
     INITIAL LOAD
  ========================= */

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    try {
      const [overviewRes, configRes] = await Promise.all([
        getMaintenanceOverview(),
        getMaintenanceConfig(),
      ]);

      setDevices(overviewRes.devices || []);
      setFirstServiceAlert(configRes.firstServiceAlert || 0);
      setServiceInterval(configRes.serviceInterval || 0);
      setWarningWindow(configRes.warningWindow || 0); // ✅ NEW
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  /* =========================
     SAVE CONFIG
  ========================= */

  async function saveConfig() {
    try {
      await updateMaintenanceConfig({
        firstServiceAlert,
        serviceInterval,
        warningWindow, // ✅ NEW
      });
      toast.success("Maintenance config updated");
      loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  }

  /* =========================
     SEND REMINDER
  ========================= */

  async function handleSendReminder(device) {
    try {
      await sendMaintenanceReminder({
        manufacturerId: device.manufacturerId,
        totalBales: device.totalBales,
        alertBales: device.nextCheckupAt,
        status: device.maintenanceStatus,
      });

      toast.success(`Reminder sent to ${device.owner?.name || "Owner"}`);
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function handleResolveMaintenance(device) {
  try {
    await resolveMaintenanceNotification({
      manufacturerId: device.manufacturerId,
    });

    toast.success(`Maintenance resolved for device ${device.manufacturerId}`);

    // 🔥 Refresh dashboard
    loadAll();
  } catch (e) {
    toast.error(e.message);
  }
}


  /* =========================
     SEARCH FILTER
  ========================= */

  const filteredDevices = devices.filter((d) => {
    const q = search.toLowerCase();
    return (
      d.manufacturerId?.toLowerCase().includes(q) ||
      d.deviceId?.toLowerCase().includes(q) ||
      d.owner?.name?.toLowerCase().includes(q)
    );
  });

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [search]);

  /* =========================
     PAGINATION LOGIC
  ========================= */

  const totalPages = Math.ceil(filteredDevices.length / PAGE_SIZE) || 1;

  const paginatedDevices = filteredDevices.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE
  );

  // 🔒 KEY FIX: filler rows to prevent row stretching
  const emptyRows = Math.max(0, PAGE_SIZE - paginatedDevices.length);

  /* =========================
     KPI COUNTS
  ========================= */

  const devicesOk = filteredDevices.filter(
    (d) => d.maintenanceStatus === "OK"
  ).length;

  const checkupAlert = filteredDevices.filter(
    (d) => d.maintenanceStatus === "ALERT"
  ).length;

  const checkupDue = filteredDevices.filter(
    (d) => d.maintenanceStatus === "DUE"
  ).length;


  const isActive = (d) => d.subscriptionPhase === "active";
  const isExpiring = (d) => d.subscriptionPhase === "expiring";
  const isGrace = (d) => d.subscriptionPhase === "grace";
  const isEnded = (d) => d.subscriptionPhase === "ended";
  /* =========================
     LOADING
  ========================= */

  if (loading) {
    return <p style={{ padding: 24 }}>Loading maintenance overview...</p>;
  }

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="dashboard-wrapper">

      {/* ===== KPI CARDS ===== */}
      <div className="dashboard-cards">
        <SummaryCard title="Total Devices" value={filteredDevices.length} type="total" />
        <SummaryCard title="Devices OK" value={devicesOk} type="ok" />
        <SummaryCard title="Checkup Alert" value={checkupAlert} type="alert" />
        <SummaryCard title="Checkup Due" value={checkupDue} type="due" />
      </div>

      {/* ===== CONFIG TOOLBAR ===== */}
      <div className="table-toolbar compact-toolbar">
      <div className="config-field">
        <label>First Service Alert (Initial trigger)</label>
        <input
          type="number"
          className="service-input"
          value={firstServiceAlert}
          onChange={(e) => setFirstServiceAlert(Number(e.target.value))}
        />
      </div>

      <div className="config-field">
        <label>Service Interval (Next cycle distance)</label>
        <input
          type="number"
          className="service-input"
          value={serviceInterval}
          onChange={(e) => setServiceInterval(Number(e.target.value))}
        />
      </div>

      <div className="config-field">
        <label>Warning Window (Alert before due)</label>
        <input
          type="number"
          className="service-input"
          value={warningWindow}
          onChange={(e) => setWarningWindow(Number(e.target.value))}
        />
      </div>

      <button className="save-config-btn" onClick={saveConfig}>
        💾 Save Config
      </button>

      <input
        type="text"
        className="search-input"
        placeholder="Search device / owner"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
      {/* ===== TABLE ===== */}
      <div className="device-table-wrapper">
        <table className="device-table">
          <thead>
            <tr>
              <th>Machine Serial number</th>
              <th>Owner</th>
              <th>Total Bales</th>
              <th>Last Checkup Date</th>
              <th>Last Checkup</th>
              <th>Next Checkup At</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
  {paginatedDevices.map((d) => (
    <React.Fragment key={d.deviceId}>

      {/* ===== MAIN DEVICE ROW ===== */}
      <tr>
        <td className="link">{d.manufacturerId}</td>

        <td>
          {d.owner?.name ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span>{d.owner.name}</span>
              <button
                type="button"
                onClick={() => setOwnerDetails(d.owner)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
                title="View owner details"
              >
                <Info size={16} />
              </button>
            </div>
          ) : "-"}
        </td>

        <td>{isEnded(d) ? "-" : d.totalBales}</td>

        <td>
          {isEnded(d)
            ? "-"
            : d.lastCheckup
            ? formatDate(d.lastCheckup)
            : "-"}
        </td>

        <td>{isEnded(d) ? "-" : d.lastCheckBales}</td>

        <td>{isEnded(d) ? "-" : d.nextCheckupAt ?? "-"}</td>

        <td>
          <span className={`status-chip ${d.maintenanceStatus.toLowerCase()}`}>
            {d.maintenanceStatus}
          </span>
        </td>

        <td>
          {isEnded(d) && (
            <span className="sub-chip expired">
              🔴 Subscription Ended
            </span>
          )}

          {isGrace(d) && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {(d.maintenanceStatus === "ALERT" ||
                d.maintenanceStatus === "DUE") && (
                <>
                  <button
                    className={`reminder-btn ${d.maintenanceStatus.toLowerCase()}`}
                    onClick={() => handleSendReminder(d)}
                  >
                    Send Reminder
                  </button>

                  <button
                    className="resolve-btn"
                    onClick={() => handleResolveMaintenance(d)}
                  >
                    Resolve
                  </button>
                </>
              )}

              <span className="sub-chip expired">
                🔴 Subscription Expired
              </span>
            </div>
          )}

          {isExpiring(d) && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {(d.maintenanceStatus === "ALERT" ||
                d.maintenanceStatus === "DUE") && (
                <>
                  <button
                    className={`reminder-btn ${d.maintenanceStatus.toLowerCase()}`}
                    onClick={() => handleSendReminder(d)}
                  >
                    Send Reminder
                  </button>

                  <button
                    className="resolve-btn"
                    onClick={() => handleResolveMaintenance(d)}
                  >
                    Resolve
                  </button>
                </>
              )}

              <span className="sub-chip warning">
                ⚠ Subscription ends soon
              </span>
            </div>
          )}

          {isActive(d) && (
            <>
              {(d.maintenanceStatus === "ALERT" ||
                d.maintenanceStatus === "DUE") && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className={`reminder-btn ${d.maintenanceStatus.toLowerCase()}`}
                    onClick={() => handleSendReminder(d)}
                  >
                    Send Reminder
                  </button>

                  <button
                    className="resolve-btn"
                    onClick={() => handleResolveMaintenance(d)}
                  >
                    Resolve
                  </button>
                </div>
              )}

              {d.maintenanceStatus === "OK" && (
                <span style={{ color: "#999", fontSize: 13 }}>—</span>
              )}
            </>
          )}
        </td>
      </tr>

      {/* ===== ⭐ DELIVERY TRACKER SECOND ROW ===== */}
      {d.deliveryChain && d.deliveryChain.length > 0 && (
        <tr className="delivery-row">
          <td colSpan="8">
            <DeliveryTracker chain={d.deliveryChain} />
          </td>
        </tr>
      )}

    </React.Fragment>
  ))}

  {/* filler rows */}
  {Array.from({ length: emptyRows }).map((_, i) => (
    <tr key={`empty-${i}`} className="empty-row">
      <td colSpan="8" />
    </tr>
  ))}

  {paginatedDevices.length === 0 && (
    <tr>
      <td colSpan="8" className="no-records">
        No records found
      </td>
    </tr>
  )}
</tbody>
        </table>
      </div>

      {/* ===== OWNER DETAILS DIALOG ===== */}
      {ownerDetails && (
        <div className="modal-overlay" onClick={() => setOwnerDetails(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Owner Details</h3>
            <p><strong>Name:</strong> {ownerDetails.name || "-"}</p>
            <p><strong>Phone:</strong> {ownerDetails.phoneNumber || "-"}</p>
            <p><strong>Address:</strong> {ownerDetails.address || "-"}</p>
            <p><strong>Role:</strong> {formatRole(ownerDetails.role)}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                onClick={() => setOwnerDetails(null)}
                style={{
                  background: "var(--primary-color, #006337)",
                  color: "#fff",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== PAGINATION ===== */}
      <div className="maintenance-pagination">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          disabled={page === 0}
        >
          Previous
        </button>

        <span>
          Page {page + 1} of {totalPages}
        </span>

        <button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
          disabled={page + 1 >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* =========================
   ROLE FORMATTER
========================= */

function formatRole(role) {
  if (!role) return "-";
  const cleaned = role.replace(/_owner$/i, "");
  return cleaned
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/* =========================
   SUMMARY CARD
========================= */

function SummaryCard({ title, value, type }) {
  return (
    <div className={`summary-card ${type}`}>
      <p className="card-title">{title}</p>
      <h2 className="card-value">{value}</h2>
    </div>
  );
}
